import Tooltip from "@mui/material/Tooltip";
import { isClippedTableText } from "_shared/_views/tables/table-cell-tooltip";
import Typography, { type TypographyProps } from "@mui/material/Typography";
import type { Theme } from "@mui/material/styles";
import { useRef, useState, type ReactNode } from "react";

import { NoValueDash } from "_shared/_views/tables/no-value-dash";

type FeeScheduleTableCellTextProps = {
  children: ReactNode;
  color?: TypographyProps["color"];
  emphasized?: boolean;
  title?: string;
};

/** Shared visual treatment for text rendered in fee-schedule tables. */
export function FeeScheduleTableCellText(props: Readonly<FeeScheduleTableCellTextProps>) {
  const { children, color, title } = props;
  const emphasized = props.emphasized ?? false;
  const tooltip = title ?? (typeof children === "string" || typeof children === "number" ? String(children) : "");
  const textRef = useRef<HTMLSpanElement>(null);
  const [tooltipOpen, setTooltipOpen] = useState(false);

  const updateTooltip = () => {
    const text = textRef.current;
    setTooltipOpen(Boolean(
      tooltip && text && (text.scrollWidth > text.clientWidth || text.scrollHeight > text.clientHeight
        || Array.from(text.querySelectorAll<HTMLElement>("*")).some(isClippedTableText))
    ));
  };

  return (
    <Tooltip
      open={tooltipOpen}
      title={tooltip}
      onClose={() => setTooltipOpen(false)}
      disableFocusListener
      disableTouchListener
    >
      <Typography
        ref={textRef}
        color={color}
        component="span"
        variant="body2"
        onMouseEnter={updateTooltip}
        onMouseLeave={() => setTooltipOpen(false)}
        sx={{
          display: "block",
          fontWeight: emphasized ? 500 : 400,
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          whiteSpace: "nowrap",
          width: "100%",
        }}
      >
        {children}
      </Typography>
    </Tooltip>
  );
}

/**
 * The dash a fee schedule cell shows where it has no value — the Per column on a fixed-fee row,
 * where the unit is legitimately absent. See `NoValueDash` for why it is an SVG and never the
 * `remove` icon-font ligature.
 */
export function FeeScheduleTableCellNoValue() {
  return <NoValueDash />;
}

/**
 * How every cell of a removed row reads, in any collection table: struck through, in the disabled
 * tone, like a removed redline row. The fee schedule, the document collections and the team rosters
 * all draw their removed rows with it, so a removal looks the same on every tab (#6995). Apply it to
 * each data cell of the row, never to its controls.
 */
export function removedTableRowCellStyles(theme: Theme) {
  return {
    color: theme.palette.text.disabled,
    textDecoration: "line-through",
    textDecorationColor: theme.palette.text.primary,
    textDecorationThickness: "2px",
    "& .MuiTypography-root": {
      color: "inherit",
      textDecoration: "inherit",
    },
  };
}

type FeeScheduleTableCellStyleOptions = {
  columnId?: string;
  truncate?: boolean;
};

/** Keeps plain values and nested MUI/redline inputs on the same body2 scale. */
export function feeScheduleTableCellStyles(
  theme: Theme,
  { columnId, truncate = true }: FeeScheduleTableCellStyleOptions = {},
) {
  if (columnId === "mrt-row-actions" || columnId === "locked") return {};

  const truncation = truncate
    ? {
      overflow: "hidden",
      textOverflow: "ellipsis",
      whiteSpace: "nowrap",
    }
    : {};

  return {
    fontSize: theme.typography.body2.fontSize,
    lineHeight: theme.typography.body2.lineHeight,
    minWidth: 0,
    ...truncation,
    "& .MuiTypography-root": {
      fontSize: "inherit",
      lineHeight: "inherit",
      minWidth: 0,
      ...truncation,
    },
    // MUI InputBase sets its own 1rem font size. Override the root as well as
    // the custom input so redline cells stay on the same body2 scale as plain cells.
    "& .MuiInputBase-root, & .MuiInputBase-input": {
      fontSize: "inherit",
      lineHeight: "inherit",
    },
    ...(truncate && {
      "& .MuiInputBase-input > div": {
        minWidth: 0,
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
      },
    }),
  };
}
